import { BodyText, toast } from "@/components/ui";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { formatAddress } from "@/lib/formatters";
import { useState } from "react";
import type { z } from "zod";
import {
  type Profile,
  useProfileAvailableActions,
  useUpdateProfileAddress,
} from "../network/useProfile";
import { ProfileActionType } from "../network/useProfile";
import type { profileAddressFormSchema } from "../schemas";
import { UpdateAddressForm } from "./UpdateAddressForm";

export const AddressEditor = ({
  patientId,
  profile,
}: { patientId: string; profile: Profile }) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const onSuccess = () => {
    setIsEditingAddress(false);
    toast.success("Address updated");
  };
  const { mutate: updateProfileAddress, isPending: isUpdatingAddress } =
    useUpdateProfileAddress(patientId, onSuccess);

  const { data: availableActions } = useProfileAvailableActions(patientId);

  const isDisabled =
    !availableActions?.[ProfileActionType.UPDATE_ADDRESS]?.allowed;

  const handleSubmit = (values: z.infer<typeof profileAddressFormSchema>) => {
    const apiAddress: {
      addressLine_1: string;
      addressLine_2?: string;
      city: string;
      state: string;
      postalCode: string;
    } = {
      addressLine_1: values.addressLine_1,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
    };

    // Only include addressLine2 if it has a value
    if (values.addressLine_2?.trim()) {
      apiAddress.addressLine_2 = values.addressLine_2;
    }

    updateProfileAddress({ address: apiAddress });
  };

  if (isEditingAddress) {
    return (
      <UpdateAddressForm
        handleSubmit={handleSubmit}
        address={profile.address}
        isUpdatingAddress={isUpdatingAddress}
        setIsEditingAddress={setIsEditingAddress}
      />
    );
  }
  return (
    <EditableRecordButton
      title="Home Address"
      onClick={() => {
        setIsEditingAddress(true);
      }}
      disabled={isDisabled}
    >
      <BodyText size="sm">
        {profile?.address ? formatAddress(profile?.address) : "No address"}
      </BodyText>
    </EditableRecordButton>
  );
};
